import {
  CircleDot,
  Square,
  Cog,
  Box,
  Radio,
  Scale,
  Eye,
  CheckSquare,
  User,
  UserRound,
  Layers,
  ChevronRight,
  Gauge,
} from "lucide-react";
import type { CSSProperties } from "react";
import { Handle, Position, type NodeProps } from "reactflow";
import { NodeResizer } from "@reactflow/node-resizer";
import "@reactflow/node-resizer/dist/style.css";
import { cn } from "@/lib/utils";

type MachineStatus = "RUNNING" | "STOPPED" | "IDLE" | "NODATA";

interface BaseNodeData {
  label: string;
  status?: MachineStatus;
  machineId?: string;
  requiresOperator?: boolean;
  sensorId?: string;
  subtype?: string;
  unit?: string;
  description?: string;
  role?: string;
  color?: string;
  iconKey?: string;
  pending?: boolean;
  clickable?: boolean;
  hasProductionCounter?: boolean;
}

function statusBorder(status?: MachineStatus): string {
  switch (status) {
    case "RUNNING":
      return "border-status-running";
    case "STOPPED":
      return "border-status-stopped";
    case "IDLE":
      return "border-status-idle";
    case "NODATA":
    default:
      return "border-muted-foreground/40";
  }
}

function statusRibbonVar(status?: MachineStatus): string {
  switch (status) {
    case "RUNNING":
      return "var(--rd-success-500)";
    case "STOPPED":
      return "var(--rd-danger-500)";
    case "IDLE":
      return "var(--rd-warning-500)";
    case "NODATA":
    default:
      return "var(--border-strong)";
  }
}

// Exportado p/ reuso pelo canvas do sandbox de simulação (SandboxMachineNode).
export function NodeShell({
  selected,
  children,
  className,
  status,
  pending,
}: {
  selected?: boolean;
  children: React.ReactNode;
  className?: string;
  status?: MachineStatus;
  pending?: boolean;
}) {
  return (
    <div
      className={cn(
        "relative rounded-md border bg-card px-3 py-2 shadow-rd-sm transition-all",
        pending
          ? "border-2 border-dashed [border-color:var(--rd-danger-500)]"
          : `border ${statusBorder(status)}`,
        "motion-safe:hover:[box-shadow:0_0_0_1.5px_var(--brand-500),0_0_0_4px_oklch(50%_0.18_285_/_0.18)]",
        selected && "[box-shadow:0_0_0_2px_var(--brand-500),0_0_0_6px_oklch(50%_0.18_285_/_0.18)]",
        className
      )}
      data-pending={pending ? "true" : undefined}
    >
      {}
      {!pending ? (
        <span
          aria-hidden="true"
          className="absolute inset-x-0 top-0 h-[2px] rounded-t-md bg-[var(--ribbon-bg)]"
          style={{ "--ribbon-bg": statusRibbonVar(status) } as CSSProperties}
        />
      ) : null}
      {children}
    </div>
  );
}

export function MachineNode({ data, selected }: NodeProps<BaseNodeData>) {
  return (
    <NodeShell
      selected={selected}
      status={data.status}
      pending={data.pending}
      className="min-w-[160px]"
    >
      <Handle type="target" position={Position.Left} />
      <div className="flex items-center gap-2 pt-0.5">
        <Cog className="h-4 w-4 text-text-tertiary" />
        <div className="flex min-w-0 flex-1 flex-col">
          <span
            className={cn(
              "truncate text-sm font-medium leading-tight",
              data.pending && "text-rd-danger-700"
            )}
          >
            {data.label}
          </span>
          {data.status ? (
            <span className="text-[10px] font-semibold uppercase tracking-widest text-text-tertiary">
              {data.status === "RUNNING"
                ? "Em produção"
                : data.status === "STOPPED"
                  ? "Parada"
                  : data.status === "IDLE"
                    ? "Ociosa"
                    : "Sem comunicação"}
            </span>
          ) : null}
        </div>
        {data.requiresOperator ? (
          // Indicador acessível (ícone + tooltip, nunca só cor).
          <span
            title="Exige operador presente"
            aria-label="Exige operador presente"
            data-testid="requires-operator-badge"
            className="flex h-5 w-5 shrink-0 items-center justify-center rounded-full bg-muted"
          >
            <UserRound className="h-3.5 w-3.5 text-text-secondary" />
          </span>
        ) : null}
        {data.hasProductionCounter ? (
          // Máquina com contador de produção (ícone + tooltip, nunca só cor).
          <span
            title="Conta produção da linha"
            aria-label="Conta produção da linha"
            data-testid="production-counter-badge"
            className="flex h-5 w-5 shrink-0 items-center justify-center rounded-full bg-muted"
          >
            <Gauge className="h-3.5 w-3.5 text-text-secondary" />
          </span>
        ) : null}
        {data.clickable ? (
          <ChevronRight className="h-4 w-4 shrink-0 text-text-tertiary group-hover:translate-x-0.5 motion-safe:transition-transform" />
        ) : null}
      </div>
      <Handle type="source" position={Position.Right} />
    </NodeShell>
  );
}

export function EquipmentNode({ data, selected }: NodeProps<BaseNodeData>) {
  return (
    <NodeShell selected={selected} className="min-w-[140px] border-muted-foreground/40">
      <Handle type="target" position={Position.Left} />
      <div className="flex items-center gap-2">
        <Box className="h-4 w-4 text-muted-foreground" />
        <span className="text-sm font-medium">{data.label}</span>
      </div>
      {data.subtype ? <p className="text-[10px] text-muted-foreground">{data.subtype}</p> : null}
      <Handle type="source" position={Position.Right} />
    </NodeShell>
  );
}

export function SensorNode({ data, selected }: NodeProps<BaseNodeData>) {
  return (
    <NodeShell selected={selected} className="min-w-[120px] border-blue-500/60">
      <Handle type="target" position={Position.Top} />
      <div className="flex items-center gap-2">
        <Radio className="h-4 w-4 text-blue-600 dark:text-blue-400" />
        <span className="text-sm font-medium">{data.label}</span>
      </div>
      <Handle type="source" position={Position.Bottom} />
    </NodeShell>
  );
}

export function ScaleNode({ data, selected }: NodeProps<BaseNodeData>) {
  return (
    <NodeShell selected={selected} className="min-w-[120px] border-amber-500/60">
      <Handle type="target" position={Position.Bottom} />
      <div className="flex items-center gap-2">
        <Scale className="h-4 w-4 text-amber-600 dark:text-amber-400" />
        <div className="flex flex-col">
          <span className="text-sm font-medium">{data.label}</span>
          {data.unit ? (
            <span className="text-[10px] text-muted-foreground">{data.unit}</span>
          ) : null}
        </div>
      </div>
      <Handle type="source" position={Position.Top} />
    </NodeShell>
  );
}

export function ControlPointNode({ data, selected }: NodeProps<BaseNodeData>) {
  return (
    <NodeShell selected={selected} className="min-w-[140px] border-yellow-500/60">
      <Handle type="target" position={Position.Top} />
      <div className="flex items-center gap-2">
        <Eye className="h-4 w-4 text-yellow-700 dark:text-yellow-400" />
        <span className="text-sm font-medium">{data.label}</span>
      </div>
      <Handle type="source" position={Position.Bottom} />
    </NodeShell>
  );
}

export function InspectionNode({ data, selected }: NodeProps<BaseNodeData>) {
  return (
    <NodeShell selected={selected} className="min-w-[140px] border-purple-500/60">
      <Handle type="target" position={Position.Left} />
      <div className="flex items-center gap-2">
        <CheckSquare className="h-4 w-4 text-purple-600 dark:text-purple-400" />
        <span className="text-sm font-medium">{data.label}</span>
      </div>
      <Handle type="source" position={Position.Right} />
    </NodeShell>
  );
}

export function HumanStepNode({ data, selected }: NodeProps<BaseNodeData>) {
  return (
    <NodeShell selected={selected} className="min-w-[140px] border-teal-500/60">
      <Handle type="target" position={Position.Left} />
      <div className="flex items-center gap-2">
        <User className="h-4 w-4 text-teal-600 dark:text-teal-400" />
        <div className="flex flex-col">
          <span className="text-sm font-medium">{data.label}</span>
          {data.role ? (
            <span className="text-[10px] text-muted-foreground">{data.role}</span>
          ) : null}
        </div>
      </div>
      <Handle type="source" position={Position.Right} />
    </NodeShell>
  );
}

export function GroupNode({ data, selected }: NodeProps<BaseNodeData>) {
  return (
    <NodeShell
      selected={selected}
      className="h-full min-h-[120px] w-full min-w-[200px] border-dashed border-muted-foreground/60 bg-muted/30"
    >
      <NodeResizer minWidth={200} minHeight={120} isVisible={selected} />
      <div className="flex items-center gap-2">
        <Layers className="h-4 w-4 text-muted-foreground" />
        <span className="text-sm font-semibold">{data.label}</span>
      </div>
    </NodeShell>
  );
}

export function StartNode({ data, selected }: NodeProps<BaseNodeData>) {
  return (
    <div
      className={cn(
        "flex h-12 w-12 items-center justify-center rounded-full border-2 border-status-running bg-status-running/10 text-status-running",
        selected && "ring-2 ring-primary ring-offset-1"
      )}
      title={data.label}
    >
      <CircleDot className="h-5 w-5" />
      <Handle type="source" position={Position.Right} />
    </div>
  );
}

export function EndNode({ data, selected }: NodeProps<BaseNodeData>) {
  return (
    <div
      className={cn(
        "flex h-12 w-12 items-center justify-center rounded-full border-2 border-status-stopped bg-status-stopped/10 text-status-stopped",
        selected && "ring-2 ring-primary ring-offset-1"
      )}
      title={data.label}
    >
      <Square className="h-5 w-5" />
      <Handle type="target" position={Position.Left} />
    </div>
  );
}

export const nodeTypes = {
  MACHINE: MachineNode,
  EQUIPMENT: EquipmentNode,
  SENSOR: SensorNode,
  SCALE: ScaleNode,
  CONTROL_POINT: ControlPointNode,
  INSPECTION: InspectionNode,
  HUMAN_STEP: HumanStepNode,
  GROUP: GroupNode,
  START: StartNode,
  END: EndNode,
} as const;
